import React, { useState } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { projects } from '../mockData';

const Work = () => {
  const [selectedProject, setSelectedProject] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const openProject = (project) => {
    setSelectedProject(project);
    setCurrentImageIndex(0);
    document.body.style.overflow = 'hidden';
  };

  const closeProject = () => {
    setSelectedProject(null);
    setCurrentImageIndex(0);
    document.body.style.overflow = 'auto';
  };

  const nextImage = () => {
    if (selectedProject) {
      setCurrentImageIndex((prev) =>
        prev === selectedProject.images.length - 1 ? 0 : prev + 1
      );
    }
  };

  const prevImage = () => {
    if (selectedProject) {
      setCurrentImageIndex((prev) =>
        prev === 0 ? selectedProject.images.length - 1 : prev - 1
      );
    }
  };

  return (
    <div className="bg-[#f5f2ed] pt-24">
      {/* Hero */}
      <section className="section-padding">
        <div className="max-w-[1600px] mx-auto">
          <h1 className="text-5xl md:text-7xl font-['Playfair_Display'] text-[#1a1a1a] mb-6 fade-in">
            Selected Work
          </h1>
          <p className="text-xl text-[#9c9590] max-w-3xl fade-in" style={{ animationDelay: '0.2s' }}>
            A curated selection of projects across residential and commercial — each built to the same uncompromising standard.
          </p>
        </div>
      </section>

      {/* Residential Projects */}
      <section className="section-padding bg-white">
        <div className="max-w-[1600px] mx-auto">
          <div className="mb-12">
            <h2 className="text-4xl md:text-5xl font-['Playfair_Display'] text-[#1a1a1a] mb-3">
              Residential
            </h2>
            <p className="text-[#9c9590]">
              Custom homes and residences built for families who value permanence.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {projects.residential.map((project, index) => (
              <div
                key={project.id}
                className="group cursor-pointer fade-in"
                style={{ animationDelay: `${index * 0.1}s` }}
                onClick={() => openProject(project)}
              >
                <div className="relative h-[400px] overflow-hidden mb-4">
                  <img
                    src={project.images[0]}
                    alt={project.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                </div>
                <h3 className="text-2xl font-['Playfair_Display'] text-[#1a1a1a] mb-2">
                  {project.name} — {project.location}
                </h3>
                <p className="text-[#9c9590] text-sm">{project.tagline}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Commercial Projects */}
      <section className="section-padding">
        <div className="max-w-[1600px] mx-auto">
          <div className="mb-12">
            <h2 className="text-4xl md:text-5xl font-['Playfair_Display'] text-[#1a1a1a] mb-3">
              Commercial
            </h2>
            <p className="text-[#9c9590]">
              Workspaces, retail, and hospitality — engineered for performance and presence.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {projects.commercial.map((project, index) => (
              <div
                key={project.id}
                className="group cursor-pointer fade-in"
                style={{ animationDelay: `${index * 0.1}s` }}
                onClick={() => openProject(project)}
              >
                <div className="relative h-[400px] overflow-hidden mb-4">
                  <img
                    src={project.images[0]}
                    alt={project.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                </div>
                <h3 className="text-2xl font-['Playfair_Display'] text-[#1a1a1a] mb-2">
                  {project.name} — {project.location}
                </h3>
                <p className="text-[#9c9590] text-sm">{project.tagline}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Project Detail Modal */}
      {selectedProject && (
        <div className="fixed inset-0 z-50 bg-[#1a1a1a]/95 overflow-y-auto">
          <div className="min-h-screen px-6 py-24">
            {/* Close Button */}
            <button
              onClick={closeProject}
              className="fixed top-6 right-6 text-white hover:text-[#b8986e] transition-colors z-50"
              aria-label="Close"
            >
              <X size={32} />
            </button>

            <div className="max-w-[1400px] mx-auto">
              {/* Image Gallery */}
              <div className="relative mb-12">
                <div className="relative h-[70vh] overflow-hidden">
                  <img
                    src={selectedProject.images[currentImageIndex]}
                    alt={`${selectedProject.name} - Image ${currentImageIndex + 1}`}
                    className="w-full h-full object-cover"
                  />
                </div>

                {/* Navigation Arrows */}
                {selectedProject.images.length > 1 && (
                  <>
                    <button
                      onClick={prevImage}
                      className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/10 backdrop-blur-sm text-white p-3 hover:bg-white/20 transition-colors"
                      aria-label="Previous image"
                    >
                      <ChevronLeft size={24} />
                    </button>
                    <button
                      onClick={nextImage}
                      className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/10 backdrop-blur-sm text-white p-3 hover:bg-white/20 transition-colors"
                      aria-label="Next image"
                    >
                      <ChevronRight size={24} />
                    </button>
                  </>
                )}

                {/* Image Counter */}
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-white/10 backdrop-blur-sm text-white px-4 py-2 text-sm">
                  {currentImageIndex + 1} / {selectedProject.images.length}
                </div>
              </div>

              {/* Project Details */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-12 text-white">
                {/* Main Content */}
                <div className="md:col-span-2">
                  <h2 className="text-4xl md:text-5xl font-['Playfair_Display'] mb-4">
                    {selectedProject.name}
                  </h2>
                  <p className="text-xl text-[#b8986e] mb-8">
                    {selectedProject.tagline}
                  </p>
                  <p className="text-[#9c9590] leading-relaxed text-lg">
                    {selectedProject.description}
                  </p>
                </div>

                {/* Project Info */}
                <div className="space-y-6">
                  <div>
                    <h3 className="text-sm tracking-widest uppercase text-[#b8986e] mb-2">
                      Location
                    </h3>
                    <p className="text-white">{selectedProject.location}</p>
                  </div>
                  <div>
                    <h3 className="text-sm tracking-widest uppercase text-[#b8986e] mb-2">
                      Area
                    </h3>
                    <p className="text-white">{selectedProject.area}</p>
                  </div>
                  <div>
                    <h3 className="text-sm tracking-widest uppercase text-[#b8986e] mb-2">
                      Completed
                    </h3>
                    <p className="text-white">{selectedProject.year}</p>
                  </div>
                  {selectedProject.architect && (
                    <div>
                      <h3 className="text-sm tracking-widest uppercase text-[#b8986e] mb-2">
                        Architect Partner
                      </h3>
                      <p className="text-white">{selectedProject.architect}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Work;
